import type React from 'react'
import { useEffect, useState } from 'react'
import { StartCommunityOfPracticeType } from '../../../../../models/StartCommunityOfPracticeType'
import { toast } from 'react-toastify'
import { Modal } from '../../../../customComponents/Modal'
import { Box, InputAdornment, TextField, Typography } from '@mui/material'
import { AddStartCommunityOfPractice, UpdateStartCommunityOfPractice } from '../../../../../services/AnnualPlanService'
import { ActivityEditSession } from '../ActivityEditSession'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'

export interface AddStartCommunityOfPracticeFormValues {
  priorityId: number
  eventType: StartCommunityOfPracticeType
  numberOfAttendees: number | null
  budgetRequest: number | null
  isImplementationPhase: boolean
}

export interface UpdateStartCommunityOfPracticeFormValues extends AddStartCommunityOfPracticeFormValues {
  id: number
  isUpdate?: boolean
}

export interface StartCommunityOfPracticeFormValues {
  [StartCommunityOfPracticeType.Coaching]: AddStartCommunityOfPracticeFormValues
  [StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies]: AddStartCommunityOfPracticeFormValues
  [StartCommunityOfPracticeType.EarlyChildhood]: AddStartCommunityOfPracticeFormValues
  [StartCommunityOfPracticeType.PfaSbt]: AddStartCommunityOfPracticeFormValues
  [StartCommunityOfPracticeType.PeerToPeer]: AddStartCommunityOfPracticeFormValues
  [StartCommunityOfPracticeType.SecondaryTransition]: AddStartCommunityOfPracticeFormValues
  [StartCommunityOfPracticeType.SexualHealth]: AddStartCommunityOfPracticeFormValues
}

interface Props {
  open: boolean
  priorityId: number
  onClose: () => void
  onSaved: () => void
  initialValues?: Record<StartCommunityOfPracticeType, UpdateStartCommunityOfPracticeFormValues>
  isImplementationPhase: boolean
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const StartCommunityOfPracticeModal = (props: Props): JSX.Element => {
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const initialFormValues = (eventType: StartCommunityOfPracticeType): AddStartCommunityOfPracticeFormValues => ({
    priorityId: props.priorityId ?? 0,
    numberOfAttendees: null,
    budgetRequest: null,
    eventType,
    isImplementationPhase: props.isImplementationPhase
  })

  const [formValues, setFormValues] = useState<Record<StartCommunityOfPracticeType, AddStartCommunityOfPracticeFormValues>>({
    [StartCommunityOfPracticeType.Coaching]: initialFormValues(StartCommunityOfPracticeType.Coaching),
    [StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies]: initialFormValues(StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies),
    [StartCommunityOfPracticeType.EarlyChildhood]: initialFormValues(StartCommunityOfPracticeType.EarlyChildhood),
    [StartCommunityOfPracticeType.PfaSbt]: initialFormValues(StartCommunityOfPracticeType.PfaSbt),
    [StartCommunityOfPracticeType.PeerToPeer]: initialFormValues(StartCommunityOfPracticeType.PeerToPeer),
    [StartCommunityOfPracticeType.SecondaryTransition]: initialFormValues(StartCommunityOfPracticeType.SecondaryTransition),
    [StartCommunityOfPracticeType.SexualHealth]: initialFormValues(StartCommunityOfPracticeType.SexualHealth)
  })
  const [submitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues(props.initialValues)
    }
  }, [])

  const hasInvalidFormValues = Object.values(formValues)
    .some((formValue: AddStartCommunityOfPracticeFormValues) => formValue.numberOfAttendees == null || formValue.budgetRequest == null ||
      formValue.numberOfAttendees.toString() === '' || formValue.budgetRequest.toString() === '')

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: StartCommunityOfPracticeType): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [key]: { ...formValues[key], [name]: value } })
  }

  const handleSubmit = async (): Promise<void> => {
    if (props.initialValues != null &&
      props.initialValues[StartCommunityOfPracticeType.Coaching]?.isUpdate !== true &&
      checkThreshold()
    ) {
      setConfirmState('needsConfirmation')
      return
    }
    await continueSubmit()
  }

  const checkThreshold = (): boolean => {
    if (props.initialValues == null) return false

    return Object.values(StartCommunityOfPracticeType).some(eventType => {
      const initialBudget = props.initialValues?.[eventType]?.budgetRequest
      const currentBudget = formValues[eventType]?.budgetRequest

      // Skip comparison if either value is null/undefined
      if (initialBudget == null || currentBudget == null) return false

      // Convert to numbers and skip if initial budget is 0 (avoid division by zero)
      const initialAmount = Number(initialBudget)
      const currentAmount = Number(currentBudget)

      if (initialAmount === 0 && currentAmount !== initialAmount) return true

      // Calculate percentage change
      const changePercent = Math.abs(currentAmount - initialAmount) / initialAmount
      return changePercent > 0.1 // 10% threshold
    })
  }

  const continueSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields. If an event is not needed, enter 0.')
      return
    }

    const addFormValues: Record<string, AddStartCommunityOfPracticeFormValues> = {
      Coaching: formValues[StartCommunityOfPracticeType.Coaching],
      ComprehensiveCommunicationSupportsAndStrategies: formValues[StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies],
      EarlyChildhood: formValues[StartCommunityOfPracticeType.EarlyChildhood],
      PeerToPeer: formValues[StartCommunityOfPracticeType.PeerToPeer],
      PfaSbt: formValues[StartCommunityOfPracticeType.PfaSbt],
      SecondaryTransition: formValues[StartCommunityOfPracticeType.SecondaryTransition],
      SexualHealth: formValues[StartCommunityOfPracticeType.SexualHealth]
    }

    const updateFormValues: Record<string, UpdateStartCommunityOfPracticeFormValues> = {
      Coaching: {
        id: props.initialValues?.[StartCommunityOfPracticeType.Coaching].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.Coaching],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.Coaching].isUpdate ?? false
      },
      ComprehensiveCommunicationSupportsAndStrategies: {
        id: props.initialValues?.[StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies].isUpdate ?? false
      },
      EarlyChildhood: {
        id: props.initialValues?.[StartCommunityOfPracticeType.EarlyChildhood].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.EarlyChildhood],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.EarlyChildhood].isUpdate ?? false
      },
      PeerToPeer: {
        id: props.initialValues?.[StartCommunityOfPracticeType.PeerToPeer].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.PeerToPeer],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.PeerToPeer].isUpdate ?? false
      },
      PfaSbt: {
        id: props.initialValues?.[StartCommunityOfPracticeType.PfaSbt].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.PfaSbt],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.PfaSbt].isUpdate ?? false
      },
      SecondaryTransition: {
        id: props.initialValues?.[StartCommunityOfPracticeType.SecondaryTransition].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.SecondaryTransition],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.SecondaryTransition].isUpdate ?? false
      },
      SexualHealth: {
        id: props.initialValues?.[StartCommunityOfPracticeType.SexualHealth].id ?? 0,
        ...formValues[StartCommunityOfPracticeType.SexualHealth],
        isUpdate: props.initialValues?.[StartCommunityOfPracticeType.SexualHealth].isUpdate ?? false
      }
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateStartCommunityOfPractice(updateFormValues)
      : await AddStartCommunityOfPractice(addFormValues)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    toast.success(`Attendance Plan ${props.initialValues != null ? 'Updated' : 'Saved'}`)

    props.onClose()
    props.onSaved()
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueSubmit()
        }}
    />
    : <Modal
        title={props?.initialValues != null ? 'Update START Communities of Practice' : 'Add START Communities of Practice'}
        open={props.open}
        data-testid={props?.initialValues != null ? 'editStartCommunityOfPracticeModal' : 'addStartCommunityOfPracticeModal'}
        width='480px'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={props.onClose}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='h5'>START Communities of Practice</Typography>
            <Typography variant='body1'>Please enter the number of attendees and budget request for each Community of Practice.</Typography>

            {Object.keys(formValues).map(key => {
              const label: string = key === StartCommunityOfPracticeType.ComprehensiveCommunicationSupportsAndStrategies ? 'Communication (CCSS)' : key
              const formValue = formValues[key as StartCommunityOfPracticeType]
              const numberOfAttendeesInvalid = formValue.numberOfAttendees == null ||
                formValue.numberOfAttendees < 0 ||
                formValue.numberOfAttendees.toString() === ''
              const budgetRequestInvalid = formValue.budgetRequest == null ||
                formValue.budgetRequest < 0 ||
                formValue.budgetRequest.toString() === ''

              return <Box key={key} data-testid='startCommunityOfPracticeForm'>
                <Typography variant='body1'>{label}</Typography>
                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <TextField
                    name='numberOfAttendees'
                    label='Number Attending'
                    value={formValue.numberOfAttendees ?? ''}
                    data-testid='numberOfAttendees'
                    onChange={(e) => { handleFieldUpdate(e, key as StartCommunityOfPracticeType) }}
                    variant='filled'
                    required={numberOfAttendeesInvalid}
                    error={submitted && numberOfAttendeesInvalid}
                  />

                  <TextField
                    id="budget-request"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    name='budgetRequest'
                    label="Budget Request"
                    variant='filled'
                    value={formValue.budgetRequest ?? ''}
                    data-testid='budgetRequest'
                    onChange={(e) => { handleFieldUpdate(e, key as StartCommunityOfPracticeType) }}
                    required={budgetRequestInvalid}
                    error={submitted && budgetRequestInvalid}
                  />
                </Box>
              </Box>
            })}

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues[StartCommunityOfPracticeType.Coaching].id}
                activityName='START Community Of Practice'
                onClose={props.onClose}
              />
            }
          </Box>
        }
    />
  )
}
